import { format, parseISO } from 'date-fns';
import { formatDateMain } from '@hero/core';

export const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en', { style: 'currency', currency: 'USD' }).format(value);

export const formatDateTime = (dateString: string) => {
  if (!dateString) return '';
  try {
    const date = parseISO(dateString);
    return format(date, 'EEE dd MMM, hh:mm a');
  } catch (error) {
    console.error('Error parsing date:', error);
    return '';
  }
};

/**
 * Formats a date label with relative labels (Today/Tomorrow) without time.
 * Uses formatDateMain from @hero/core for relative date detection.
 * @param dateString - ISO date string or Date object
 * @param t - Translation function (optional) for translating "Today" and "Tomorrow"
 * @returns Formatted date label (e.g., "Today", "Tomorrow", or formatted date)
 */
export const formatRelativeDateLabel = (
  dateString: string | Date | null | undefined,
  t?: (key: string, fallback?: string) => string,
): string => {
  if (!dateString) return '';

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (!date || isNaN(date.getTime())) return '';

    // Use formatDateMain to get relative date (Today/Tomorrow) or formatted date
    const formattedDate = formatDateMain({
      date,
      format: 'relative_without_time',
      ifDateisNotRelative: 'date',
    });

    // Translate "Today" and "Tomorrow" if translation function is provided
    if (formattedDate === 'Today') {
      return t ? t('date.today', 'Today') : 'Today';
    } else if (formattedDate === 'Tomorrow') {
      return t ? t('date.tomorrow', 'Tomorrow') : 'Tomorrow';
    } else {
      return formattedDate;
    }
  } catch (error) {
    console.error('Error formatting relative date label:', error);
    return '';
  }
};

/**
 * Formats a date with relative labels (Today/Tomorrow) and time.
 * Uses formatDateMain from @hero/core for relative date detection.
 * @param dateString - ISO date string or Date object
 * @param t - Translation function (optional) for translating "Today" and "Tomorrow"
 * @returns Formatted date string with time (e.g., "Today 10:00 AM" or "Tomorrow 2:30 PM")
 */
export const formatRelativeDateTime = (
  dateString: string | Date | null | undefined,
  t?: (key: string, fallback?: string) => string,
): string => {
  if (!dateString) return '';

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (!date || isNaN(date.getTime())) return '';

    // Get the date label
    const dateLabel = formatRelativeDateLabel(date, t);

    // Get time string
    const timeStr = format(date, 'h:mm a');

    return `${dateLabel} ${timeStr}`;
  } catch (error) {
    console.error('Error formatting relative date:', error);
    return '';
  }
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// fille uploader
export const convertFileToUrl = (file: File) => URL.createObjectURL(file);

const ACCEPTED_FILE_TYPES = {
  PDF: ['application/pdf'],
  IMAGE: ['image/jpeg', 'image/png', 'image/gif'],
  DOCUMENT: [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
};

// Flatten the structure for the FileUploader
export const flattenedAcceptedFileTypes = {
  ...ACCEPTED_FILE_TYPES.PDF.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
  ...ACCEPTED_FILE_TYPES.IMAGE.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
  ...ACCEPTED_FILE_TYPES.DOCUMENT.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
};

const ACCEPTED_IMAGE_TYPES = {
  IMAGE: ['image/jpeg', 'image/png', 'image/gif'],
};

export const flattenedAcceptedImageTypes = {
  ...ACCEPTED_IMAGE_TYPES.IMAGE.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
};

// Formate Vehicle Type
export function formatVehicleText(id: string) {
  return id
    .split('_') // Split the string by underscores
    .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
    .join(' '); // Join the words with spaces
}

// Formate Temprature
export function formatTempratureText(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** Mask a phone number for OTP confirmation screens. */
export function maskPhoneNumber(phone: string): string {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length <= 5) return `+${cleaned}`;
  return `+${cleaned.slice(0, 3)} *** ${cleaned.slice(-3)}`;
}

// Format phone number to add "+" and proper spacing
export const formatPhoneNumber = (phone: string) => {
  if (!phone) return '';

  // Remove any non-digit characters
  const cleaned = phone.replace(/\D/g, '');

  // Add the plus sign if it doesn't exist
  if (!cleaned.startsWith('966')) {
    return `+${cleaned}`;
  }

  // Format numbers starting with 966 (Saudi numbers)
  return `+${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
};

// Helper function to convert File to base64
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

// Helper function to convert array of files to base64
export const convertFilesToBase64 = async (
  files: File[] | undefined,
): Promise<string | undefined> => {
  if (!files || files.length === 0) return undefined;
  try {
    const base64String = await fileToBase64(files[0]);
    return base64String;
  } catch (error) {
    console.error('Error converting file to base64:', error);
    return undefined;
  }
};
